import React, { useState } from 'react';
import { Button, Icon, Skeleton } from '@innovationnorway/cl-react';
import useOneWayIn from '../../../hooks/use-one-way-in';
import { useParams, Link } from 'react-router-dom';
import { PageParams } from '../../types/PageParams';
import { useTranslation } from 'react-i18next';
import useOrgInfo from '../../hooks/use-org-info';
import { useExternalCompany } from 'src/hooks/use-external-company';
import ConnectCompanyModal from '../ConnectCompanyModal/ConnectCompanyModal';
import { ROUTES } from 'src/navigation';
import InfoBox from './InfoBox';

const ContactInfo = () => {
  const { formId } = useParams<PageParams>();
  const { oneWayInGroupedFormWOEnrollmentsQuery: form } = useOneWayIn(formId);
  const { organizationNumber, organizationName } = useOrgInfo(formId || '');
  const { data: company } = useExternalCompany(organizationNumber ?? '');
  const [activeDialogue, setActiveDialogue] = useState<'ConnectCompany' | null>(null);
  const [emailCopied, setEmailCopied] = useState(false);
  const { oneWayInOpportunityById: opportunity } = useOneWayIn(formId);
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');

  const contact = form.data?.kontakt;
  const companyName = organizationName || company?.name;
  const municipality = form.data?.organisasjon?.kommune;

  const renderCompanyName = () => {
    if (!companyName) {
      return <span>{tOneWayIn('opportunityDetails.CompanyMissing')}</span>;
    }

    if (opportunity.data?.clusterMemberId) {
      return (
        <Link to={ROUTES.RESOURCE_BASE.MEMBERS.DETAILS(opportunity.data.clusterMemberId).BASE}>
          {companyName}
        </Link>
      );
    }

    return companyName;
  };

  if (form.isLoading) {
    return (
      <InfoBox heading={tCommon('contactInformation')}>
        <div className="display--flex flex-direction--column gap--1">
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      </InfoBox>
    );
  }

  return (
    <InfoBox heading={tCommon('contactInformation')}>
      <div className="display--flex flex-direction--column gap--1">
        <ContactInfoRow className={!contact?.navn ? 'color--ds-text-subtle' : undefined} iconName="user">
          {contact?.navn ?? tOneWayIn('opportunityDetails.NameMissing')}
        </ContactInfoRow>
        <ContactInfoRow
          iconName="building"
          className={!companyName ? 'color--ds-text-subtle' : undefined}
          action={
            !companyName && (
              <Button
                className="padding-x--1 opportunity-overview--contact-row-button"
                icon
                variant="tertiary"
                onClick={() => setActiveDialogue('ConnectCompany')}
              >
                <Icon prefix="fasr" iconName="plus" />
                {tOneWayIn('connectCompany')}
              </Button>
            )
          }
        >
          {renderCompanyName()}
        </ContactInfoRow>
        <ContactInfoRow
          className={!municipality ? 'color--ds-text-subtle' : undefined}
          iconName="location-dot"
        >
          {municipality ?? tOneWayIn('opportunityDetails.MunicipalityMissing')}
        </ContactInfoRow>
        <ContactInfoRow className={!contact?.telefon ? 'color--ds-text-subtle' : undefined} iconName="phone">
          {contact?.telefon ?? tOneWayIn('opportunityDetails.PhoneMissing')}
        </ContactInfoRow>
        <ContactInfoRow
          iconName="envelope"
          className={!contact?.epost ? 'color--ds-text-subtle' : undefined}
          action={
            contact?.epost && (
              <Button
                className="opportunity-overview--contact-row-button"
                icon
                variant="tertiary"
                aria-label={
                  emailCopied ? tCommon('copied') : `${tCommon('copy')} ${tCommon('email').toLowerCase()}`
                }
                onClick={() => {
                  navigator.clipboard.writeText(contact.epost!);
                  setEmailCopied(true);
                  setTimeout(() => setEmailCopied(false), 3000);
                }}
              >
                <Icon prefix="fasr" iconName={emailCopied ? 'check' : 'copy'} />
              </Button>
            )
          }
        >
          {contact?.epost ?? tOneWayIn('opportunityDetails.EmailMissing')}
        </ContactInfoRow>
      </div>
      {opportunity.data && (
        <ConnectCompanyModal
          show={activeDialogue === 'ConnectCompany'}
          onClose={() => setActiveDialogue(null)}
        />
      )}
    </InfoBox>
  );
};

const ContactInfoRow = ({
  iconName,
  children,
  action,
  className,
}: {
  iconName: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className="display--flex flex-direction--row align-items--center justify-content--space-between gap--1">
      <div
        className={`display--flex flex-direction--row align-items--center gap--1${className ? ` ${className}` : ''}`}
      >
        <Icon prefix="fasr" iconName={iconName} />
        {children}
      </div>
      {action}
    </div>
  );
};

export default ContactInfo;
